import { MessageIcon, PencilSimpleIcon, CopyIcon } from '@c/icons';
import AddAddressModal from '@c/modals/AddAddressModal';
import { PhoneIcon } from '@heroicons/react/20/solid';
import { OrderDocument } from '@models/order';
import { PublicUserDocument } from '@models/user';
import { useQuery } from '@tanstack/react-query';
import Button from '@ui/Button';
import Chip from '@ui/Chip';
import SafeImage from '@ui/SafeImage';
import { updateOrderByKey } from '@util/firestore/orders';
import {
  getPublicUserDoc,
  getUserById,
  updateUserMetricsByKey,
} from '@util/firestore/users';
import { useAuth } from 'context/AuthContext';
import { useChat } from 'context/ChatContext';
import { useToastContext } from 'context/ToastContext';
import { FieldValue, increment } from 'firebase/firestore';
import Link from 'next/link';
import React from 'react';

const CustomerAndShippingInfo = ({
  order,
  variant,
}: {
  order: OrderDocument;
  variant: 'buyer' | 'seller';
}) => {
  const { data: buyer } = useQuery({
    queryKey: ['publicUser', order.buyer_id],
    queryFn: () => getPublicUserDoc({ uid: order.buyer_id }),
    enabled: !!order.buyer_id,
  });
  const { openChatModalWithOrder } = useChat();
  const { userDoc } = useAuth();
  return (
    <div className="flex w-full flex-col items-start overflow-clip border-[1px] border-solid border-[#D9D9D9] bg-white sm:rounded-[1.5rem]">
      <Header variant={variant} />
      {buyer && (
        <div className="flex w-full flex-col gap-[0.8rem] p-[2rem]">
          <Link
            href={`/profile/${buyer.username_slug}`}
            className="flex items-center gap-[1.6rem]"
            target="_blank"
          >
            <SafeImage
              src={buyer.photo}
              width={30}
              height={30}
              alt="user profile image"
              className="aspect-square rounded-full object-cover"
            />
            <span className="text-[1.8rem]">{buyer.username}</span>
            {order.is_guest && userDoc?.roles?.support && (
              <Chip color="purple" text="Guest" />
            )}
          </Link>

          <span className="text-[1.4rem] text-[#333333]">
            Contact Information
          </span>
          <span className="flex items-center gap-[1.6rem]">
            <PhoneIcon
              width={24}
              height={24}
              fill={'none'}
              stroke={'#333333'}
            />
            {order.address.phone}
          </span>
          <ShippingInfo order={order} />
        </div>
      )}
      {(variant === 'seller' || userDoc?.roles?.support) && (
        <div className="p-[2rem]">
          <Button
            type="outline"
            className="h-[4rem] py-0"
            onClick={() => {
              openChatModalWithOrder(order, order.buyer_id);
            }}
          >
            Message Buyer
          </Button>
        </div>
      )}
      {userDoc?.roles?.support && (
        <div className="p-[2rem]">
          <Button
            type="secondary"
            className="h-[4rem] py-0"
            onClick={async () => {
              try {
                const new_uid = prompt('Enter the new buyer uid');
                if (new_uid) {
                  const user = await getUserById(new_uid);
                  if (!user) {
                    throw new Error('User not found');
                  }
                  await updateOrderByKey(order.id, 'buyer_id', new_uid);
                  await updateUserMetricsByKey(
                    new_uid,
                    'num_purchased',
                    increment(1)
                  );
                  await updateUserMetricsByKey(
                    order.buyer_id,
                    'num_purchased',
                    increment(-1)
                  );
                  await updateUserMetricsByKey(
                    order.buyer_id,
                    'ltv_purchased',
                    increment(-order.total)
                  );
                  await updateUserMetricsByKey(
                    new_uid,
                    'ltv_purchased',
                    increment(order.total)
                  );
                  if (!user.metrics.first_purchase) {
                    await updateUserMetricsByKey(
                      order.buyer_id,
                      'first_purchase',
                      order.created
                    );
                  }
                  if (!user.metrics.last_purchase) {
                    await updateUserMetricsByKey(
                      order.buyer_id,
                      'last_purchase',
                      order.created
                    );
                  }
                  alert('Buyer transferred successfully');
                }
              } catch (error) {
                alert((error as Error).message);
              }
            }}
          >
            Transfer Buyer
          </Button>
        </div>
      )}
    </div>
  );
};

const Header = ({ variant }: { variant: 'buyer' | 'seller' | 'admin' }) => {
  return (
    <div className="flex w-full flex-wrap items-center justify-between gap-[1.6rem] whitespace-nowrap border-b-[1px] border-solid border-[#D9D9D9] bg-[#f0f2f2] p-[2rem]">
      <span className="text-[2.2rem] font-semibold">
        {variant === 'buyer' ? 'Shipping Info' : 'Customer & Shipping Info'}
      </span>
    </div>
  );
};

const ShippingInfo = ({ order }: { order: OrderDocument }) => {
  const { userDoc } = useAuth();
  const [showAddressForm, setShowAddressForm] = React.useState(false);
  const { showSuccessToast } = useToastContext();
  return (
    <>
      <span className="flex justify-between text-[1.4rem] text-[#333333]">
        Shipping Address
        {userDoc?.roles?.support && (
          <>
            <button onClick={() => setShowAddressForm(true)}>
              <PencilSimpleIcon />
            </button>
            {showAddressForm && (
              <AddAddressModal
                isOpen={showAddressForm}
                dismiss={(address) => {
                  setShowAddressForm(false);
                  if (address) {
                    updateOrderByKey(order.id, 'address', address);
                  }
                }}
                address={order.address}
                skipValidation={true}
              />
            )}
          </>
        )}
      </span>
      <div className="relative flex w-full flex-col gap-[0.8rem]">
        <span>{order.address.name}</span>
        <span>{order.address.address_line1}</span>
        <span>{order.address.address_line2}</span>
        <span>
          {order.address.city_locality}, {order.address.state_province},{' '}
          {order.address.postal_code}
        </span>
        <span>{order.address.country_code}</span>
        <span>{order.address.phone}</span>
        <button
          className="absolute right-4 top-4 z-20"
          onClick={() => {
            navigator.clipboard.writeText(
              `${order.address.name}\n${order.address.address_line1}\n${order.address.address_line2}\n${order.address.city_locality}, ${order.address.state_province} ${order.address.postal_code}\n${order.address.country_code}`
            );
            showSuccessToast('Address copied to clipboard');
          }}
        >
          <CopyIcon className="h-8 w-8" />
        </button>
      </div>
    </>
  );
};
export default CustomerAndShippingInfo;
